import React, { useCallback, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  getDay,
  getDaysInMonth,
  isSameDay,
  isToday,
  startOfMonth,
} from "date-fns";

import { env } from "~/env";
import { SliderArrowButton } from "~/shared";
import { CalendarDay } from "~/shared.types";
import { icons } from "~/ui";
import { tw } from "~/utils";
import { formatMonthYear, getCalendarDayAriaLabel } from "../utils";
import { CalendarPopupSkeleton } from "./calendar-pop-up-skeleton";

type CalendarPopupProps = {
  onClose: () => void;
  onDaySelect: (day: CalendarDay) => void;
  calendarDays?: CalendarDay[];
  isSelectable: (date: Date) => boolean;
  selectedDay?: CalendarDay;
  onMonthChange?: (monthYear: { month: string; year: number }) => void;
  initialDate?: Date;
  isPending?: boolean;
  className?: string;
};

export const CalendarPopup = ({
  onClose,
  onDaySelect,
  calendarDays = [],
  isSelectable,
  selectedDay,
  onMonthChange,
  initialDate,
  isPending = false,
  className,
}: CalendarPopupProps) => {
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [opener] = useState(() => document.activeElement);
  const today = new Date();
  const maxAllowedDate = new Date(
    today.getFullYear(),
    today.getMonth() + Number(env.VITE_APPOINTMENT_SLOT_MAX_MONTHS),
    1,
  );

  const isMaxMonth =
    currentDate.getFullYear() === maxAllowedDate.getFullYear() &&
    currentDate.getMonth() === maxAllowedDate.getMonth();

  // Helper function to find matching day from calendarDays
  const findMatchingDay = useCallback(
    (date: Date): CalendarDay | null => {
      return calendarDays.find((day) => isSameDay(day.date, date)) || null;
    },
    [calendarDays],
  );

  const getMonthDays = (date: Date) => {
    const daysInMonth = getDaysInMonth(date);
    const startingDay = getDay(startOfMonth(date));

    return { daysInMonth, startingDay };
  };

  const goToPreviousMonth = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1,
    );
    setCurrentDate(newDate);
    onMonthChange?.({
      month: newDate.toLocaleDateString("en-US", { month: "long" }),
      year: newDate.getFullYear(),
    });
  };

  const goToNextMonth = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1,
    );
    setCurrentDate(newDate);
    onMonthChange?.({
      month: newDate.toLocaleDateString("en-US", { month: "long" }),
      year: newDate.getFullYear(),
    });
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    );

    // Check if the day is available
    const matchingDay = findMatchingDay(newDate);
    if (matchingDay && !matchingDay.isAvailable) {
      return; // Don't allow selection of unavailable days
    }

    // Check if the day has appointments
    if (!isSelectable(newDate)) {
      return; // Don't allow selection of days without appointments
    }

    // Immediately select the date and confirm
    onDaySelect(matchingDay as CalendarDay);
  };

  const { daysInMonth, startingDay } = getMonthDays(currentDate);
  const monthYear = formatMonthYear(currentDate);

  // Check if current month is the current month
  const isCurrentMonth =
    currentDate.getFullYear() === today.getFullYear() &&
    currentDate.getMonth() === today.getMonth();

  // Create an array for empty days
  const emptyDays = useMemo(() => {
    return Array.from({ length: startingDay }, (_, index) => (
      <div key={`empty-${index}`} className="h-10 text-neutral-800" />
    ));
  }, [startingDay]);

  // Create an array of days in the month
  const days = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1; // Days are 1-indexed
      const currentDateObj = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      const matchingDay = findMatchingDay(currentDateObj);
      const hasAppointmentsForDate = isSelectable(currentDateObj);

      // Determine styling based on matching day data
      let buttonClasses =
        "text-blueGrey flex size-10 items-center justify-center rounded-full text-sm transition-all";

      // Check if this day is selected

      const isSelected =
        selectedDay && isSameDay(selectedDay.date, currentDateObj);

      if (isSelected) {
        buttonClasses += " bg-violet text-white";
      } else if (matchingDay) {
        if (matchingDay.isAvailable) {
          buttonClasses += hasAppointmentsForDate
            ? " bg-lightLavender/50 text-indigo hover:bg-lightLavender cursor-pointer"
            : "";
        } else {
          buttonClasses += " cursor-not-allowed";
        }
      } else {
        buttonClasses += hasAppointmentsForDate
          ? " bg-lightLavender/50 text-indigo hover:bg-lightLavender cursor-pointer"
          : "";
      }

      // Determine if button should be disabled
      const isDisabled =
        (matchingDay && !matchingDay.isAvailable) || !hasAppointmentsForDate;

      // Return the button for the day
      return (
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          disabled={isDisabled}
          className={buttonClasses}
          aria-label={getCalendarDayAriaLabel(currentDateObj, {
            isSelected: !!isSelected,
            isUnavailable: !!isDisabled,
          })}
          title={
            isDisabled
              ? "This day is not available"
              : matchingDay && !matchingDay.isAvailable
                ? "This day is not available"
                : !hasAppointmentsForDate
                  ? "No appointments available"
                  : `Select ${day}`
          }
        >
          <div className="flex flex-col items-center">
            <span className="text-sm font-medium">{day}</span>
            {isToday(currentDateObj) && !isSelected && (
              <div className="bg-violet mt-0.5 size-1.5 rounded-full" />
            )}
          </div>
        </button>
      );
    });
  }, [daysInMonth, currentDate, findMatchingDay, selectedDay, isSelectable]); // Recalculate when daysInMonth or currentDate changes

  // Combine empty days and actual days into a single array
  const allDays = [...emptyDays, ...days];

  return (
    <Dialog.Root open onOpenChange={onClose}>
      <Dialog.Overlay
        className={tw(
          "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4",
          className,
        )}
      >
        <Dialog.Content
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (opener instanceof HTMLElement) opener.focus();
          }}
          className="bg-calendar-background w-full max-w-sm rounded-lg p-2"
        >
          <div className="flex min-h-[430px] grow flex-col rounded-lg bg-white p-2">
            <div className="mb-4 flex items-center justify-end">
              <Dialog.Close
                aria-label="Close calendar"
                className="text-gray-400 hover:text-gray-600"
              >
                <icons.outline.XMarkIcon className="size-6" />
              </Dialog.Close>
            </div>

            <div className="mb-4 flex max-w-sm items-center justify-between px-10">
              {/* Arrows */}
              <SliderArrowButton
                onClick={goToPreviousMonth}
                ariaLabel="Previous month"
                variant="primary"
                disabled={isCurrentMonth}
              >
                <icons.outline.ArrowLeftIcon className="size-6" />
              </SliderArrowButton>

              <Dialog.Title className="text-indigo/70 font-sans font-medium">
                {monthYear}
              </Dialog.Title>
              <SliderArrowButton
                onClick={goToNextMonth}
                ariaLabel="Next month"
                variant="primary"
                disabled={isMaxMonth}
              >
                <icons.outline.ArrowRightIcon className="size-6" />
              </SliderArrowButton>
            </div>

            <div className="relative grid grid-cols-7 gap-1">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="flex h-10 items-center justify-center text-sm font-medium text-gray-500"
                >
                  {day}
                </div>
              ))}

              {isPending ? <CalendarPopupSkeleton /> : allDays}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Root>
  );
};
